import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { ActionDecision } from "@/models/underwriting";

const DECISION_OPTIONS: { value: ActionDecision; label: string }[] = [
  { value: "Approved", label: "Approved" },
  { value: "Declined", label: "Declined" },
  { value: "Alternative", label: "Alternative action taken" },
  { value: "MoreInfo", label: "More info needed" },
];

interface ActionRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Reference shown in the dialog title. */
  applicationId: string | number;
  /** When set, the decision is pre-selected and the dropdown is locked. */
  lockedDecision?: ActionDecision;
  onSubmit: (decision: ActionDecision, notes: string) => Promise<void>;
}

export function ActionRequestDialog({ open, onOpenChange, applicationId, lockedDecision, onSubmit }: ActionRequestDialogProps) {
  const [decision, setDecision] = useState<ActionDecision | "">(lockedDecision ?? "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Highlight the reason field once a submit attempt failed on an empty reason.
  const notesError = error === "Please provide a reason before submitting." && !notes.trim();

  // Sync the (possibly locked) decision when it changes — not on every open,
  // so outside-click/Escape closes don't wipe a decision the user already made.
  useEffect(() => {
    setDecision(lockedDecision ?? "");
  }, [lockedDecision]);

  const reset = () => {
    setDecision(lockedDecision ?? "");
    setNotes("");
    setError(null);
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!decision) {
      setError("Please select an outcome.");
      return;
    }
    if (!notes.trim()) {
      setError("Please provide a reason before submitting.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(decision, notes.trim());
      onOpenChange(false);
      reset();
    } catch {
      setError("Failed to submit the action. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Action Request — {applicationId}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="uw-action-decision"
              className="text-[11px] font-semibold text-muted-foreground"
            >
              Decision
            </label>
            <select
              id="uw-action-decision"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-slate-600"
              value={decision}
              onChange={(e) => setDecision(e.target.value as ActionDecision | "")}
              disabled={submitting || !!lockedDecision}
            >
              <option value="">— Select outcome —</option>
              {DECISION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="uw-action-notes"
              className="text-[11px] font-semibold text-muted-foreground"
            >
              Reason <span className="text-rose-600">*</span>
            </label>
            <textarea
              id="uw-action-notes"
              rows={4}
              className={cn(
                "w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 resize-none",
                notesError
                  ? "border-rose-500 focus:ring-rose-500"
                  : "border-border focus:ring-slate-600"
              )}
              placeholder="Add details about the decision and any instructions for the agent…"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                if (error) setError(null);
              }}
              disabled={submitting}
              aria-invalid={notesError}
            />
          </div>

          {error && <p className="text-sm text-rose-600 font-medium">{error}</p>}
        </div>

        <DialogFooter className="gap-3 sm:gap-3">
          <Button variant="outline" disabled={submitting} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting && <Loader2 className="size-3.5 animate-spin mr-1" />}
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
